import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import { useObserverPeopleQuery, useObserverStartMutation } from '@platform/features/auth';
import { trapTab } from '@platform/ui/focusTrap';
import { authErrorKey } from '../auth/authErrorKey';
import { authRoutes } from '../auth/authRoutes';
import { useEnterApp } from '../auth/useEnterApp';
import { ObserverRow } from './ObserverRow';
import { useDebounced } from './useDebounced';
import './observer.css';

/**
 * Вибір особи режиму спостерігача (прототип v4 obsPick) над екраном входу: «Режим спостерігача · Відкрийте застосунок від імені будь-якої
 * особи · лише перегляд, нічого не змінюється», пошук «за іменем або ел. поштою», «Народні депутати · N» (усі особи цього виду), до 120
 * рядків у порядку сервера. Вибір відкриває сесію спостерігача (POST /observer/sessions) і демо-офіс особи; «×», Esc чи клік по тлу — до
 * входу (UIUX layout/dialogs: фокус — у полі пошуку й не виходить за межі діалогу). Пошук осіб сервер обмежує за частотою (рішення 4
 * етапу 8): на 429 — «Забагато спроб…», останні знайдені рядки лишаються.
 */
export function ObserverPicker() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const enter = useEnterApp();
  const [q, setQ] = useState('');
  const query = useDebounced(q.trim(), 200);
  const { data, error: peopleError } = useObserverPeopleQuery(query);
  const [start, starting] = useObserverStartMutation();
  const [error, setError] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const close = () => navigate(authRoutes.login(), { replace: true });
  useEffect(() => {
    search.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate(authRoutes.login(), { replace: true });
      else if (box.current) trapTab(box.current, e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);
  const pick = (personId: string) => {
    if (starting.isLoading) return;
    setError('');
    setPicked(personId);
    start(personId).unwrap().then((s) => enter(routes.home(), undefined, s.workspaceId), (e: { code?: string }) => {
      setPicked(null);
      setError(t(authErrorKey(e.code)));
    });
  };
  const kind = data ? `${t(data.kind === 'mps' ? 'observer.mps' : 'observer.demo')} · ${data.total}` : t('observer.loading');
  return (
    <div className="obs-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div ref={box} className="obs-dialog" role="dialog" aria-modal="true" aria-label={t('observer.title')}>
        <div className="obs-head">
          <div className="obs-titles"><strong className="obs-title">{t('observer.title')}</strong><span className="obs-sub">{t('observer.sub')}</span></div>
          <button type="button" className="obs-close" aria-label={t('observer.close')} onClick={close}>×</button>
        </div>
        <div className="obs-search">
          <input ref={search} className="obs-input" value={q} placeholder={t('observer.search')} aria-label={t('observer.searchLabel')} onChange={(e) => setQ(e.target.value)} />
          <span className="obs-count">{kind}</span>
        </div>
        {(error || peopleError) && <div className="obs-error" role="alert">{error || t(authErrorKey((peopleError as { code?: string }).code))}</div>}
        <div className="obs-list">
          {(data?.items ?? []).slice(0, 120).map((p) => <ObserverRow key={p.id} person={p} busy={starting.isLoading} picked={p.id === picked} onPick={pick} />)}
        </div>
      </div>
    </div>
  );
}
